import React from 'react'
import {
  Alert,
  Collapse,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { ChevronDownIcon } from '@radix-ui/react-icons'

import { ScriptBlock } from '@/components/Event/CrForm/components/ScriptBlock'

// A ScriptBlock the SA reads aloud ONLY if the Court Reporter asked them to announce
// going on/off the record (sa.announce_record). Gating what's visible — rather than just
// styling it — is what stops an SA from reading the block on autopilot.
//   true  -> the script, shown normally
//   false -> a subdued note; the script is collapsed behind a chevron in case it's
//            still needed mid-event
//   null  -> a prompt to answer the announce question first (transient; the field is
//            required once the CR has joined)
export const ConditionalScriptBlock: React.FC<{
  heading?: string
  announceRecord: boolean | null
  children: React.ReactNode
}> = ({ heading, announceRecord, children }) => {
  const [opened, { toggle }] = useDisclosure(false)

  if (announceRecord === true) {
    return <ScriptBlock heading={heading ?? ''}>{children}</ScriptBlock>
  }

  if (announceRecord === false) {
    return (
      <Stack spacing="xs">
        <Alert color="gray" variant="light">
          <Stack>
            <Group
              position="apart"
              noWrap
              align="center"
              onClick={toggle}
              sx={{ cursor: 'pointer' }}
            >
              <Text size="sm">
                The Court Reporter did not ask you to announce the record.
                <br />
                Script block hidden. Click to expand.
              </Text>
              <UnstyledButton sx={{ flex: 'none' }}>
                <Group spacing={4} noWrap>
                  <ChevronDownIcon
                    style={{
                      transform: opened ? 'rotate(180deg)' : undefined,
                      transition: 'transform 150ms',
                    }}
                  />
                </Group>
              </UnstyledButton>
            </Group>
            <Collapse in={opened}>
              <ScriptBlock heading={heading ?? ''}>{children}</ScriptBlock>
            </Collapse>
          </Stack>
        </Alert>
      </Stack>
    )
  }

  return (
    <Alert color="gray" variant="light">
      <Text size="sm" color="dimmed">
        Answer the announce-record question in the Attendance step to see this.
      </Text>
    </Alert>
  )
}
